import { useEffect, useRef, useState } from 'react';

const reasons = [
  {
    icon: 'ri-scales-3-line',
    title: 'Admitted High Court Expertise',
    body: 'Our firm is led by Havy Mohuba, an admitted attorney of the High Court of South Africa, with qualifications from the University of the Witwatersrand, University of Pretoria, and Harvard University.',
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'Comprehensive Legal Coverage',
    body: 'From commercial and corporate law to RAF claims, mining law, and criminal defence — we offer end-to-end legal representation across eight specialised practice areas under one roof.',
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Client-Centred Representation',
    body: 'Every matter is handled with the urgency, discretion, and professionalism it deserves. We do not treat clients as case numbers — we treat them as people whose rights and interests must be protected.',
  },
];

export default function Mission() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-16 md:py-24 bg-white overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-gradient-to-br from-[#6A1B9A]/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-gradient-to-tl from-[#1976D2]/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#6A1B9A] mb-3">
            Why Choose Us
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4 leading-tight">
            Why Choose Mohuba &amp; Partners Inc
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Polokwane&apos;s trusted legal firm — serving individuals, families, and corporations across Limpopo.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {reasons.map((item, index) => (
            <div
              key={index}
              className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-400 cursor-default ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 0.12}s` }}
            >
              {/* Card Inner Padding */}
              <div className="p-7 md:p-8">
                {/* Icon */}
                <div className="w-12 h-12 flex items-center justify-center mb-5 rounded-xl bg-[#6A1B9A]/8" style={{ background: 'rgba(106,27,154,0.08)' }}>
                  <i className={`${item.icon} text-2xl text-[#6A1B9A]`}></i>
                </div>

                {/* Heading */}
                <h3 className="text-base md:text-lg font-bold text-[#2C2C2C] mb-3 leading-snug group-hover:text-[#6A1B9A] transition-colors duration-300">
                  {item.title}
                </h3>

                {/* Body */}
                <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                  {item.body}
                </p>
              </div>

              {/* Bottom accent bar */}
              <div className="h-0.5 w-0 group-hover:w-full bg-[#6A1B9A] transition-all duration-400"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
